import React, { useContext, useEffect, useState } from "react";
import {
    Col,
    Icon,
    Input,
    InputNumber,
    message,
    Modal,
    Radio,
    Row,
    Spin,
    Tooltip
} from "antd";
import style from "./index.module.scss";
import Form, { useForm } from "rc-field-form";
import { errorMessage, isTrue } from "service/service";
import { CourseContext } from "../../../Store";
import { ResultMessage } from "components/ResultMessage";
import {
    saveExercise,
    getExercise,
    anewPublishExercise
} from "service/exercise";
import useRequest from "utils/useRequest";
import moment from "moment";
import { useHistory } from "react-router-dom";
import useRequestFn from "utils/useRequestFn";
import { ModalProps } from "antd/lib/modal/Modal";
import StuPicker from "./Components/StuPicker";

interface FormFields {
    description: string;
    limitedTime: 1 | 0;
    validityTime: number;
}

interface ExerciseModalProps extends ModalProps {
    type: "republish" | "modify" | "create";
    visible: boolean;
    onOk: () => void;
    onCancel: () => void;
    id: number;
}

export default function ExerciseModal({
    type,
    visible,
    onOk,
    onCancel,
    id,
    ...props
}: ExerciseModalProps) {
    const [form] = useForm<FormFields>();
    const [values, setValues] = useState<FormFields>();
    const { basename, course } = useContext(CourseContext);
    const weekTimeId = course.weekTimeId;
    const [data, { loading }, doFn] = useRequest(() => getExercise(id), {
        autoStart: false
    });
    const [callSaveExercise, saveStates] = useRequestFn(saveExercise);

    // 重新发布
    const [onRepublish, republishState] = useRequestFn(async (values: any) => {
        const { validityTime, studentIds } = values;
        if (values.limitedTime === 1 && values.validityTime < 1)
            return message.error("请填写活动时长");

        try {
            await anewPublishExercise({
                id,
                weekTimeId,
                validityTime,
                studentIds
            });
            onOk();
            onCancel();
            ResultMessage.success("重新发布成功!");
            history.push(`${basename}/exercise/detail/${id}`);
        } catch (e) {
            message.error(errorMessage(e));
        }
    });

    const history = useHistory();

    useEffect(() => {
        if (data?.validityTime)
            setValues({ ...form.getFieldsValue(), limitedTime: 1 });
    }, [data]);

    useEffect(() => {
        if (id) doFn();
    }, [id]);

    function onFieldsChange() {
        setValues(form.getFieldsValue());
    }

    function onOkClick() {
        form.submit();
    }

    // 点击保存按钮
    async function onFinish(values: any) {
        if (type === "republish") return onRepublish(values);
        return onSave(values);
    }

    // 保存
    async function onSave(values: any) {
        const { description, validityTime } = values;
        if (values.limitedTime === 1 && values.validityTime < 1)
            return message.error("请填写活动时长");

        const title =
            description.trim().length < 1
                ? `${moment().format("MM-DD HH:mm")} 练习活动`
                : description;
        try {
            const res = await callSaveExercise({
                weekTimeId,
                description: title,
                validityTime,
                id: data?.id ? data.id : ""
            });
            onOk();
            onCancel();
            if (id) ResultMessage.success("修改成功!");
            if (!id && res.data)
                history.push(`${basename}/exercise/topics/${res.data}`);
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    const FormComponent = (
        <Form form={form} onFinish={onFinish} onFieldsChange={onFieldsChange}>
            {["create", "modify"].includes(type) && (
                <Row>
                    <Col span={8} className={style.label}>
                        名称
                    </Col>

                    <Col span={12}>
                        <Form.Field
                            name="description"
                            initialValue={data?.description || ""}>
                            <Input
                                placeholder="请输入活动名称，最多可输入500个字符"
                                maxLength={500}
                            />
                        </Form.Field>
                    </Col>
                </Row>
            )}

            {type === "republish" && (
                <Row>
                    <Col span={8} className={style.label}>
                        发布对象
                    </Col>

                    <Col span={12}>
                        <Form.Field name="studentIds">
                            <StuPicker id={id} weekTimeId={weekTimeId} />
                        </Form.Field>
                    </Col>
                </Row>
            )}

            <Row>
                <Col span={8} className={style.label}>
                    活动时长
                </Col>

                <Col span={12}>
                    <Form.Field
                        name="limitedTime"
                        initialValue={data?.validityTime ? 1 : 0}>
                        <Radio.Group>
                            <Radio value={0}>不限时</Radio>
                            <Radio value={1}>限时</Radio>
                        </Radio.Group>
                    </Form.Field>

                    {isTrue(values?.limitedTime) && (
                        <>
                            <Form.Field
                                name="validityTime"
                                initialValue={data?.validityTime || 10}
                                preserve={false}>
                                <InputNumber min={1} precision={0} />
                            </Form.Field>{" "}
                            分钟&nbsp;
                        </>
                    )}

                    <Tooltip title="系统根据设置的活动时长自动结束练习，活动时长为“不限”时，表示需要教师手动结束活动">
                        <Icon
                            type="exclamation-circle"
                            className={style.notice}
                        />
                    </Tooltip>
                </Col>
            </Row>
        </Form>
    );

    if (id)
        return (
            <Modal
                visible={visible}
                title={type === "republish" ? "重新发布设置" : "练习设置"}
                okText="确认"
                className={style.root}
                width={655}
                onOk={onOkClick}
                onCancel={onCancel}
                maskClosable={false}
                okButtonProps={{ disabled: loading }}
                cancelButtonProps={{ disabled: loading }}
                confirmLoading={saveStates.loading || republishState.loading}
                {...props}>
                {loading && (
                    <div style={{ textAlign: "center", marginTop: "60px" }}>
                        <Spin size="large" />
                    </div>
                )}
                {!loading && data && FormComponent}
            </Modal>
        );

    return (
        <Modal
            visible={visible}
            title="新建练习"
            okText="确认"
            className={style.root}
            width={655}
            onOk={onOkClick}
            onCancel={onCancel}
            maskClosable={false}
            confirmLoading={saveStates.loading}
            {...props}>
            {FormComponent}
        </Modal>
    );
}

ExerciseModal.defaultProps = {
    id: ""
};
